import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import iconBack from '../../../images/icon-chevron-left.svg';
import SearchIcon from '../../../images/icon-search.svg';
import NoUnits from '../../../images/no-units-found.svg';
import { IRootState } from '../../../redux/rootReducer';
import TinyLoader from '../../common/tiny-loader/TinyLoader';
import Loader from '../../common/loader/Loader';
import Toast from '../../common/toast/Toast';
import { useDebounce } from '../../../utils/useDebounce';
import styles from './UnitsByLocationId.module.css';
import Tooltip from '../../common/tooltip/Tooltip';
import { LockerData } from '../../units/unitscard/CustomUnitCard';
import KeyLockerIcon from '../../svgIcons/KeyLockerIcon';
// import UnitsList from './unitsList/UnitsList';

interface LocationState {
    type?: string;
    locationName?: string;
    provisionId?: string;
    locationId?: string;
    lockerType?: string;
}

/**
 * Optimized Units Selection Component for New Location Availability Flow
 * 
 * Features:
 * - Proper error and loading state management
 * - Debounced search with reset functionality
 * - Error handling with user-friendly messages
 * - Optimized state management
 */
const UnitsForNewLocationAvailability: React.FC = () => {
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const locationState = useLocation().state as LocationState | null;

    // Get location data from Redux as fallback
    const locationData = useSelector((state: IRootState) => state.locationReducer.location.data);

    const type = locationState?.type || 'add-availability';
    const locationId = locationState?.locationId || '';
    const provisionId = locationState?.provisionId || '';

    // Get locationName from state or Redux as fallback
    const [locationName, setLocationName] = useState<string>(
        locationState?.locationName || locationData?.location?.name || ''
    );

    // Update locationName when locationState or locationData changes
    useEffect(() => {
        if (locationState?.locationName) {
            setLocationName(locationState.locationName);
        } else if (locationData?.location?.name && !locationName) {
            setLocationName(locationData.location.name);
        }
    }, [locationState?.locationName, locationData?.location?.name, locationName]);

    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [unitsData, setUnitsData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const debouncedSearch = useDebounce(searchText, 1000);
    const observer = useRef<IntersectionObserver | null>(null);
    const lastElementRef = useRef<HTMLTableRowElement>(null);

    // Handle keyboard input validation
    const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ' ' && searchText.length === 0) {
            event.preventDefault();
        }
    }, [searchText]);

    // Handle input change with state reset
    const handleChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const { value } = e.target as HTMLInputElement;
        setSearchText(value);
        setCurrentPage(1);
        setUnitsData([]);
        setError(null);
        setHasMore(true);
        setHasLoadedOnce(false);
    }, []);

    // Track previous search to detect actual changes
    const prevSearchRef = useRef<string>('');
    const prevLocationIdRef = useRef<string>('');

    // Reset units when search or location changes
    useEffect(() => {
        if (locationId && (prevSearchRef.current !== debouncedSearch || prevLocationIdRef.current !== locationId)) {
            setUnitsData([]);
            setCurrentPage(1);
            setHasMore(true);
            setError(null);
            prevSearchRef.current = debouncedSearch || '';
            prevLocationIdRef.current = locationId;
        }
    }, [debouncedSearch, locationId]);

    // Fetch units data - consolidated to prevent duplicate calls
    useEffect(() => {
        const fetchUnits = async () => {
            if (!locationId || !currentPage) return;

            setLoading(true);
            setError(null);

            try {
                const response = await axios.get(`/admin/units/list/${locationId}`, {
                    headers: { 'x-internal-api': 'true' },
                    params: {
                        page: currentPage,
                        limit: 12,
                        searchText: debouncedSearch || '',
                        statusFilter: 'available',
                    },
                });

                const data = response.data?.data || [];
                const totalCount = response.data?.count || 0;

                setHasMore(currentPage === 1 ? data.length < totalCount : data.length > 0);
                setHasLoadedOnce(true);

                setUnitsData((prevData) => {
                    if (currentPage === 1) {
                        return data;
                    }
                    // Avoid duplicates by filtering based on id
                    const merged = [...prevData, ...data];
                    const unique = merged.filter(
                        (obj, index, self) => index === self.findIndex((t) => t.id === obj.id)
                    );
                    // Update hasMore based on unique length
                    setHasMore(unique.length < totalCount);
                    return unique;
                });
            } catch (err) {
                const errorMessage = axios.isAxiosError(err)
                    ? err.response?.data?.message || 'Failed to fetch units'
                    : 'An unexpected error occurred';
                setError(errorMessage);
                setShowToast(true);
                console.error('Error fetching units:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUnits();
    }, [locationId, currentPage, debouncedSearch]);

    // Infinite scroll observer
    useEffect(() => {
        const node = lastElementRef.current;
        if (!node || loading || !hasMore) return;

        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    setCurrentPage((prev) => prev + 1);
                }
            },
            { threshold: 1.0 }
        );

        observer.current.observe(node);

        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [loading, hasMore]);

    // Handle unit click navigation
    const handleUnitClick = useCallback((unit: any) => {
        if (unit?.unit_status !== 'available') return;

        try {
            const targetPath = type === 'make-unit-unavailable'
                ? '/add-availability/confirm'
                : '/add-availability/booking';

            navigate(targetPath, {
                state: {
                    unitId: unit.id,
                    locationId,
                    type,
                    unitName: unit?.name,
                    nickName: unit?.nickName,
                    locationName,
                    lockerType: locationState?.lockerType,
                    provisionId
                },
            });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Navigation failed';
            setError(errorMessage);
            setShowToast(true);
        }
    }, [navigate, locationId, type, locationName, provisionId, locationState]);

    // Handle back navigation - preserve locationName if available
    const handleBackClick = useCallback(() => {
        navigate('/add-availability/locations', {
            state: {
                type,
                ...(locationName && { locationName })
            }
        });
    }, [navigate, type, locationName]);

    // Auto-hide toast
    React.useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    const title = type === 'make-unit-unavailable' ? 'Make Unit Unavailable' : 'Add Availability';

    return (
        <div className={`outletConainer ${styles.unitsContainer}`}>
            <div className={styles.inlineItems}>
                <div className={styles.leftSection}>
                    <div
                        onClick={handleBackClick}
                        className={styles.backButton}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleBackClick();
                            }
                        }}
                        aria-label="Go back to locations"
                    >
                        <img src={iconBack} alt="back icon" />
                    </div>
                    <h2>{title}</h2>
                </div>
            </div>

            <div className={styles.searchBox}>
                <div>
                    <span>Select Unit for {locationName || ""}</span>
                </div>
                <div className={styles.inputWrapper}>
                    <input
                        ref={inputRef}
                        onKeyPress={handleKeyPress}
                        value={searchText || ''}
                        onChange={handleChange}
                        type="text"
                        placeholder="Search for unit"
                        maxLength={50}
                        aria-label="Search for unit"
                    />
                    <img
                        className={styles.searchIconImage}
                        src={SearchIcon}
                        alt="search-icon"
                    />
                </div>
            </div>

            <div className={styles.locationUnits}>
                {/* Commented out UnitsList component - replaced with table view */}
                {/* <div className={styles.unitCard}>
                    <UnitsList
                        searchUnits={searchText}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                        setUnitsData={setUnitsData}
                        unitsData={unitsData}
                        setLoading={setLoading}
                        loading={loading}
                        onError={handleError}
                    />
                </div> */}

                {/* Table View for Units */}
                {loading && !hasLoadedOnce ? (
                    <div className={styles.loaderContainer}>
                        <Loader />
                    </div>
                ) : error && !hasLoadedOnce ? (
                    <div className={styles.errorContainer}>
                        <p className={styles.errorText}>{error}</p>
                    </div>
                ) : unitsData.length > 0 ? (
                    <div className={styles.unitsTableWrapper}>
                        <table className={styles.unitsTable}>
                            <thead>
                                <tr>
                                    <th>Sr. No.</th>
                                    <th>Unit Name</th>
                                    <th>Building</th>
                                    <th>Price</th>
                                    <th>Size</th>
                                    <th>Beds</th>
                                    <th>Baths</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unitsData.map((unit: any, index: number) => {
                                    const isLastElement = index === unitsData.length - 1;

                                    const uniqueLockers: string = [...new Set<number>(unit?.availability?.map((item: LockerData) => item.locker))].join(" ");
                                    return (
                                        <tr
                                            key={unit.id}
                                            ref={isLastElement ? (lastElementRef as any) : null}
                                            className={`${styles.tableRow} ${unit?.unit_status !== 'available' ? styles.disabledRow : styles.clickableRow}`}
                                            onClick={() => handleUnitClick(unit)}
                                        >
                                            <td>
                                                {index + 1}
                                            </td>
                                            <td>
                                                <div className={styles.unitNameCell}>
                                                    <span className={styles.unitId}>
                                                        <span>{unit?.unitYardiId || unit?.name}</span>
                                                        {unit?.integrationVendor === null && (
                                                            <Tooltip text='Manually created' variant='green' position='top'>
                                                                <span className={styles.badge}>M</span>
                                                            </Tooltip>
                                                        )}
                                                        {uniqueLockers && <span className={styles.badge}>
                                                            <KeyLockerIcon size={16} />
                                                            <span>{uniqueLockers}</span>
                                                        </span>}
                                                    </span>
                                                    {unit?.nickName && (
                                                        <span className={styles.unitNickname}>
                                                            &quot;{unit?.nickName}&quot;
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {unit?.buildingName ? (
                                                    <span className={styles.buildingName}>{unit.buildingName}</span>
                                                ) : (
                                                    <span className={styles.noBuilding}>—</span>
                                                )}
                                            </td>
                                            <td>
                                                {unit.price ? (
                                                    <span className={styles.price}>${unit.price}</span>
                                                ) : (
                                                    <span className={styles.noPrice}>—</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={styles.size}>{unit?.size || 0} Sq Ft</span>
                                            </td>
                                            <td>
                                                <span className={styles.beds}>
                                                    {Number(unit?.rooms) === 0
                                                        ? 'Studio'
                                                        : `${Number(unit?.rooms) || 0} ${Number(unit?.rooms) === 1 ? 'Bed' : 'Beds'}`}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={styles.baths}>
                                                    {(+unit.baths || +unit.baths === 0) &&
                                                        `${+unit.baths} ${+unit?.baths === 1 ? 'Bath' : 'Baths'}`}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`${styles.statusBadge} ${unit?.unit_status === 'available'
                                                        ? styles.statusAvailable
                                                        : unit?.unit_status === 'occupied'
                                                            ? styles.statusOccupied
                                                            : styles.statusArchived
                                                        }`}
                                                >
                                                    {unit?.unit_status === 'occupied' ? 'Occupied' : unit?.unit_status}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {loading && hasLoadedOnce && (
                            <div className={styles.loadingMore}>
                                <TinyLoader />
                            </div>
                        )}
                    </div>
                ) : hasLoadedOnce && !loading ? (
                    <div className={styles.noUnits}>
                        <img src={NoUnits} alt="no-units" />
                        <p className={styles.noUnitsFoundText}>No Units Available</p>
                    </div>
                ) : null}
            </div>

            {showToast && error && (
                <Toast type="error" message={error} />
            )}
        </div>
    );
};

export default UnitsForNewLocationAvailability;

